import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import lscache from "lscache";
import React, { ChangeEvent, SyntheticEvent, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useApartmentFunctions } from "../../hooks/apartments/useApartmentFunctions";
import IApartment from "../../types/apartment-type";
import { accommodationTypes } from "../../utils/accommodationTypes";
import AddressFields from "../Forms/Address";
import CalendarSelect from "../Forms/CalendarSelect";
import TitleDescription from "../Forms/TitleDescription";
import GroupedCountries, {
  GroupedCurrencies,
} from "../Handlers/CountryHandler";
import PhotoHandler from "../Handlers/PhotoHandler";
import MeasurementSwitch from "../ui-Components/MeasurementSwitch";

interface ApartmentFormProps {
  editing: boolean;
  rentalType: { listingType: string };
  formData: IApartment;
  measurementUnit: "m²" | "sqft";
  handleSubmit: (e: React.FormEvent) => void;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  setMeasurementUnit: React.Dispatch<React.SetStateAction<any>>;
  handleUnitChange: (newUnit: "m²" | "sqft") => void;
}

const ApartmentForm: React.FC<ApartmentFormProps> = ({
  editing,
  formData,
  rentalType,
  setFormData,
  handleSubmit,
  measurementUnit,
  handleUnitChange,
}) => {
  const intl = useIntl();
  const { TranslatedApartmentOptions } = useApartmentFunctions();
  const translatedApartmentOptions =
    TranslatedApartmentOptions(accommodationTypes);

  useEffect(() => {
    if (formData.photos.length > 0) {
      lscache.set("apartment-photos", formData.photos, 30);
    }
  }, [formData.photos]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value === null) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    lscache.set("apartment-" + name, value, 30);
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (value === null) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    lscache.set("apartment-" + name, value, 30);
  };

  const handleCurrencyChange = (
    event: SyntheticEvent,
    newCurrency: string | null
  ) => {
    setFormData({ ...formData, currency: newCurrency || "" });
    lscache.set("apartment-currency", newCurrency, 30);
  };

  const currencySymbol = Intl.NumberFormat("en-US", {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency: formData.currency || "EUR",
  })
    .formatToParts(0)
    .filter((part) => part.type === "currency")
    .map((part) => part.value)
    .join("");

  const handleCountryChange = (
    event: SyntheticEvent,
    newValue: string | null,
    newCurrency: string | null
  ) => {
    setFormData((prevState: string[]) => ({
      ...prevState,
      country: newValue || "",
      currency: newCurrency || "",
    }));
    lscache.set("apartment-country", newValue, 30);
    lscache.set("apartment-currency", newCurrency, 30);
  };

  const handleDateChange = (date: dayjs.Dayjs, name: string) => {
    if (date) {
      setFormData({ ...formData, [name]: date });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TitleDescription
            title={formData.title}
            description={formData.description}
            handleChange={handleChange}
            fieldName="title"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel htmlFor="accommodation-type-select">
              <FormattedMessage id="accomodation-type" />
            </InputLabel>
            <Select
              id="accommodation-type-select"
              data-testid="accommodation-type-select"
              name="accommodationType"
              value={formData.accommodationType || "apartment"}
              onChange={handleTypeChange}
            >
              {translatedApartmentOptions.map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                  data-testid={`menu-item-${type.value}`}
                >
                  <FormattedMessage id={type.labelId} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          {rentalType.listingType === "long-term" && (
            <FormControl fullWidth>
              <InputLabel htmlFor="aggreement-type-select">
                <FormattedMessage id="rental-period" />
              </InputLabel>
              <Select
              id="agreement-type-select"
              data-testid="agreement-type-select"
                name="duration"
                value={formData.duration || "long-term"}
                onChange={handleTypeChange}
                defaultValue={"long-term"}
              >
                <MenuItem value="long-term">
                  <FormattedMessage id="long-term" />
                </MenuItem>
                <MenuItem value="short-term">
                  <FormattedMessage id="short-term" />
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid item xs={9} md={6}>
          <TextField
            name="size"
            type="number"
            value={formData.size}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "size",
              defaultMessage: "Size (m²/sqft)",
            })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {measurementUnit}
                </InputAdornment>
              ),
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={3} md={2}>
          <MeasurementSwitch
            measurementUnit={measurementUnit}
            onUnitChange={handleUnitChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CalendarSelect
            start={dayjs(formData.availableFrom)}
            handleDateChange={(date) => handleDateChange(date, "availableFrom")}
            name="availableFrom"
            id="available-from"
            message="Date available"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl component="fieldset" style={{ display: "flex" }}>
            <FormLabel
              component="legend"
              style={{ height: "0px", fontSize: 10 }}
            >
              <FormattedMessage id="gender" />
            </FormLabel>
            <RadioGroup
              name="genderPreference"
              row
              value={formData.genderPreference || "any"}
              onChange={handleTypeChange}
              style={{ display: "flex" }}
            >
              <FormControlLabel
                value="male"
                control={<Radio />}
                label={intl.formatMessage({
                  id: "male",
                  defaultMessage: "Male",
                })}
              />
              <FormControlLabel
                value="female"
                control={<Radio />}
                label={intl.formatMessage({
                  id: "female",
                  defaultMessage: "Female",
                })}
              />
              <FormControlLabel
                defaultChecked
                value="any"
                control={<Radio />}
                label={intl.formatMessage({
                  id: "any",
                  defaultMessage: "any",
                })}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={9}>
          <AddressFields
            address={formData.address}
            city={formData.city}
            handleChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <GroupedCountries
            country={formData.country!}
            handleCountryChange={handleCountryChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            name="rent"
            type="number"
            value={formData.rent}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "rent",
              defaultMessage: "Rent",
            })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {rentalType.listingType === "long-term" ? (
                    <FormattedMessage id="per-month" />
                  ) : (
                    <FormattedMessage id="per-night" />
                  )}
                </InputAdornment>
              ),
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <GroupedCurrencies
            currency={formData.currency}
            handleCurrencyChange={handleCurrencyChange}
          />
        </Grid>
        <Grid item xs={12}>
          <PhotoHandler
            initialPhotos={formData.photos || []}
            onPhotosChange={(newPhotos) =>
              setFormData({ ...formData, photos: newPhotos })
            }
            numberOfPhotos={formData.photos?.length || 0}
            source={formData.listingType}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4} mb={2}>
          <Button id="list-apartment" variant="contained" type="submit" fullWidth>
            {editing ? (
              <FormattedMessage id="update-apartment" />
            ) : (
              <FormattedMessage id="submit-apartment" />
            )}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ApartmentForm;
